import React, { useCallback, useEffect, useState } from 'react';
import documentoLiberacaoService, { type DocumentoLiberacao } from '../services/documentoLiberacaoService';
import { UI_THEME } from '../ui/theme';
import { FIELD_CLASS } from './ui/fieldStyles';
import Button from './ui/Button';
import { toUpperText } from '../utils/formatters';

type Props = {
  emendaId: string;
  canRequest: boolean;
  canSubmit: boolean;
  canAnalyze: boolean;
  onChange?: () => void;
};

const STATUS_LABEL: Record<string, string> = {
  NAO_APLICAVEL: 'Não aplicável',
  NAO_SOLICITADO: 'Não solicitado',
  AGUARDANDO_ENVIO: 'Aguardando nota fiscal',
  EM_ANALISE: 'Documento em análise',
  APROVADO: 'Documento aprovado',
  DEVOLVIDO_CORRECAO: 'Correção solicitada',
  CANCELADO: 'Cancelado',
};

const addDays = (days: number) => {
  const value = new Date();
  value.setDate(value.getDate() + days);
  return value.toISOString().slice(0, 10);
};

const formatDate = (value?: string) => value
  ? new Date(`${value}T12:00:00`).toLocaleDateString('pt-BR')
  : '—';

const errorMessage = (error: unknown, fallback: string) => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (error as { response?: { data?: { error?: unknown } } }).response;
    if (typeof response?.data?.error === 'string') return response.data.error;
  }
  return fallback;
};

const emptyNota = { numeroNota: '', serie: '', chaveAcesso: '', valor: '' };
const onlyDigits = (value: string, limit: number) => value.replace(/\D/g, '').slice(0, limit);
const formatMoneyInput = (value: string) => {
  const digits = onlyDigits(value, 15);
  return digits ? (Number(digits) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '';
};
const parseMoneyInput = (value: string) => Number(value.replace(/\./g, '').replace(',', '.'));
const formatMoney = (value: number) => value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const DocumentoLiberacaoPanel: React.FC<Props> = ({ emendaId, canRequest, canSubmit, canAnalyze, onChange }) => {
  const isSaas = UI_THEME === 'saas';
  const [documento, setDocumento] = useState<DocumentoLiberacao | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showRequest, setShowRequest] = useState(false);
  const [nota, setNota] = useState(emptyNota);
  const [observacao, setObservacao] = useState('');
  const [request, setRequest] = useState({
    tipoDocumento: 'Nota fiscal ou documento para liberação',
    orientacao: 'Anexe o documento emitido para análise e liberação da liquidação.',
    prazoEnvio: addDays(7),
  });

  const panelClass = isSaas
    ? 'rounded-3xl border border-teal-100 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800/60'
    : 'rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900';
  const surfaceClass = 'rounded-2xl';
  const primaryClass = isSaas ? 'bg-teal-600 hover:bg-teal-700' : 'bg-sky-700 hover:bg-sky-800';
  const fieldClass = `${FIELD_CLASS} mt-1`;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDocumento(await documentoLiberacaoService.get(emendaId));
    } catch (err: unknown) {
      setError(errorMessage(err, 'Não foi possível carregar a solicitação de documento.'));
    } finally {
      setLoading(false);
    }
  }, [emendaId]);

  useEffect(() => { load(); }, [load]);

  const update = (value: DocumentoLiberacao) => {
    setDocumento(value);
    setShowRequest(false);
    setNota(emptyNota);
    setObservacao('');
    onChange?.();
  };

  const solicitar = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      update(await documentoLiberacaoService.solicitar(emendaId, request));
    } catch (err: unknown) {
      setError(errorMessage(err, 'Não foi possível enviar a solicitação.'));
    } finally {
      setSaving(false);
    }
  };

  const cadastrarNota = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      update(await documentoLiberacaoService.cadastrarNota(emendaId, {
        numeroNota: nota.numeroNota.trim(),
        serie: nota.serie.trim(),
        chaveAcesso: onlyDigits(nota.chaveAcesso, 44),
        valor: parseMoneyInput(nota.valor),
      }));
    } catch (err: unknown) {
      setError(errorMessage(err, 'Não foi possível cadastrar a nota fiscal.'));
    } finally {
      setSaving(false);
    }
  };

  const decidir = async (decisao: 'APROVAR' | 'DEVOLVER') => {
    if (decisao === 'DEVOLVER' && !observacao.trim()) {
      setError('Informe o motivo da devolução para correção.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      update(await documentoLiberacaoService.decidir(emendaId, decisao, observacao || undefined));
    } catch (err: unknown) {
      setError(errorMessage(err, 'Não foi possível registrar a análise.'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <section className={`${panelClass} p-6 text-sm text-slate-500`}>Carregando solicitação de documento...</section>;
  if (!documento) return <section className={`${panelClass} p-6 text-sm text-red-600`}>{error || 'Solicitação indisponível.'}</section>;

  const podeCadastrarNota = ['AGUARDANDO_ENVIO', 'DEVOLVIDO_CORRECAO', 'EM_ANALISE'].includes(documento.status);

  return (
    <section id="documento-liberacao" className={`${panelClass} scroll-mt-24 overflow-hidden`}>
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200 px-5 py-4 dark:border-slate-700">
        <div>
          <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Formalização para recebimento</p>
          <h2 className="mt-1 text-lg font-semibold text-slate-900 dark:text-slate-100">Documento para liberação</h2>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Solicitação à instituição, envio do documento e análise antes da liquidação.</p>
        </div>
        <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${documento.status === 'APROVADO'
          ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300'
          : documento.vencido || documento.status === 'DEVOLVIDO_CORRECAO'
            ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300'
            : 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-300'}`}>
          {documento.vencido ? 'Prazo vencido' : STATUS_LABEL[documento.status]}
        </span>
      </div>

      <div className="space-y-4 p-5">
        {!documento.disponivelParaSolicitar && documento.status === 'NAO_SOLICITADO' && (
          <div className={`${surfaceClass} border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200`}>{documento.motivoIndisponibilidade}</div>
        )}

        {documento.status === 'NAO_APLICAVEL' && (
          <p className="text-sm text-slate-600 dark:text-slate-300">Esta etapa é aplicada às execuções indiretas com instituição/OSC e Termo de Fomento.</p>
        )}

        {documento.status === 'NAO_SOLICITADO' && documento.disponivelParaSolicitar && canRequest && !showRequest && (
          <button type="button" onClick={() => setShowRequest(true)} className={`rounded-xl px-4 py-2 text-sm font-semibold text-white ${primaryClass}`}>Solicitar documento à instituição</button>
        )}

        {showRequest && (
          <form onSubmit={solicitar} className={`${surfaceClass} space-y-3 border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/40`}>
            <div className="grid gap-3 md:grid-cols-[1fr_180px]">
              <label className="text-sm text-slate-700 dark:text-slate-200">Tipo de documento<input required value={request.tipoDocumento} onChange={(event) => setRequest((prev) => ({ ...prev, tipoDocumento: toUpperText(event.target.value) }))} className={fieldClass} /></label>
              <label className="text-sm text-slate-700 dark:text-slate-200">Prazo para envio<input type="date" required value={request.prazoEnvio} onChange={(event) => setRequest((prev) => ({ ...prev, prazoEnvio: event.target.value }))} className={fieldClass} /></label>
            </div>
            <label className="block text-sm text-slate-700 dark:text-slate-200">Orientações<textarea rows={3} value={request.orientacao} onChange={(event) => setRequest((prev) => ({ ...prev, orientacao: toUpperText(event.target.value) }))} className={fieldClass} /></label>
            <div className="flex justify-end gap-2"><Button variant="secondary" onClick={() => setShowRequest(false)}>Cancelar</Button><button disabled={saving} className={`rounded-xl px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ${primaryClass}`}>{saving ? 'Enviando...' : 'Enviar solicitação'}</button></div>
          </form>
        )}

        {documento.status !== 'NAO_SOLICITADO' && (
          <div className={`${surfaceClass} grid gap-3 border border-slate-200 bg-slate-50 p-4 text-sm sm:grid-cols-3 dark:border-slate-700 dark:bg-slate-900/40`}>
            <div><span className="block text-xs uppercase text-slate-500">Documento solicitado</span><strong className="mt-1 block">{documento.tipoDocumento}</strong></div>
            <div><span className="block text-xs uppercase text-slate-500">Prazo</span><strong className="mt-1 block">{formatDate(documento.prazoEnvio)}</strong></div>
            <div><span className="block text-xs uppercase text-slate-500">Solicitado por</span><strong className="mt-1 block">{documento.solicitadoPor}</strong></div>
            {documento.orientacao && <p className="sm:col-span-3 text-slate-600 dark:text-slate-300">{documento.orientacao}</p>}
          </div>
        )}

        {podeCadastrarNota && canSubmit && (
          <form onSubmit={cadastrarNota} className={`${surfaceClass} space-y-3 border border-sky-200 bg-sky-50 p-4 dark:border-sky-800 dark:bg-sky-900/20`}>
            <div><h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">{documento.notasFiscais.length ? 'Adicionar outra nota fiscal' : 'Cadastrar nota fiscal'}</h3>{documento.observacaoAnalise && <p className="mt-1 text-sm text-red-700 dark:text-red-300">Correção solicitada: {documento.observacaoAnalise}</p>}</div>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <label className="text-sm text-slate-700 dark:text-slate-200">Número da nota<input required value={nota.numeroNota} onChange={(event) => setNota((prev) => ({ ...prev, numeroNota: toUpperText(event.target.value) }))} className={fieldClass} /></label>
              <label className="text-sm text-slate-700 dark:text-slate-200">Série<input required value={nota.serie} onChange={(event) => setNota((prev) => ({ ...prev, serie: toUpperText(event.target.value) }))} className={fieldClass} /></label>
              <label className="text-sm text-slate-700 dark:text-slate-200">Chave de acesso<input required inputMode="numeric" minLength={44} maxLength={44} value={nota.chaveAcesso} onChange={(event) => setNota((prev) => ({ ...prev, chaveAcesso: onlyDigits(event.target.value, 44) }))} className={fieldClass} /></label>
              <label className="text-sm text-slate-700 dark:text-slate-200">Valor<input required inputMode="numeric" value={nota.valor} onChange={(event) => setNota((prev) => ({ ...prev, valor: formatMoneyInput(event.target.value) }))} className={fieldClass} placeholder="0,00" /></label>
            </div>
            <div className="flex justify-end"><button disabled={saving} className={`rounded-xl px-4 py-2 text-sm font-semibold text-white disabled:opacity-50 ${primaryClass}`}>{saving ? 'Salvando...' : 'Cadastrar nota'}</button></div>
          </form>
        )}

        {documento.notasFiscais.length > 0 && (
          <div className="space-y-2">
            {documento.notasFiscais.map((item) => (
              <div key={item.id} className={`${surfaceClass} grid gap-3 border border-slate-200 bg-white p-4 text-sm sm:grid-cols-2 xl:grid-cols-4 dark:border-slate-700 dark:bg-slate-900/40`}>
                <div><span className="block text-xs uppercase text-slate-500">Número da nota</span><strong className="mt-1 block">{item.numeroNota}</strong></div>
                <div><span className="block text-xs uppercase text-slate-500">Série</span><strong className="mt-1 block">{item.serie}</strong></div>
                <div className="min-w-0"><span className="block text-xs uppercase text-slate-500">Chave de acesso</span><strong className="mt-1 block break-all">{item.chaveAcesso}</strong></div>
                <div><span className="block text-xs uppercase text-slate-500">Valor</span><strong className="mt-1 block">{formatMoney(item.valor)}</strong></div>
              </div>
            ))}
          </div>
        )}

        {documento.status === 'EM_ANALISE' && canAnalyze && (
          <div className={`${surfaceClass} space-y-3 border border-slate-200 p-4 dark:border-slate-700`}><label className="block text-sm text-slate-700 dark:text-slate-200">Observação da análise<textarea rows={3} value={observacao} onChange={(event) => setObservacao(toUpperText(event.target.value))} placeholder="Obrigatória quando devolver para correção" className={fieldClass} /></label><div className="flex justify-end gap-2"><Button variant="danger-outline" disabled={saving} onClick={() => decidir('DEVOLVER')}>Devolver para correção</Button><Button variant="success" disabled={saving} onClick={() => decidir('APROVAR')}>Aprovar documento</Button></div></div>
        )}

        {documento.status === 'APROVADO' && <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-300">Documento aprovado. A liquidação financeira está liberada.</p>}
        {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
      </div>
    </section>
  );
};

export default DocumentoLiberacaoPanel;
